import React, {useEffect, useState} from 'react'
import {useParams} from 'react-router-dom'
import ViewPersonnelForm from './ViewPersonnelForm'
import Resume from 'components/resume'
import Loader from 'components/preloader'
import {getDocuments, getSpecificDocuments} from 'api/private/documents'
import {getSpecificApplicants} from 'api/private/applicants'
import PreviewCard from 'components/preview'
import 'style/_review.scss'

/**
 * Safely parse workExperiences field from various formats
 * Handles: JSON string, "[object Object]" string, array, or undefined
 * @param {string|array|undefined} workExperiences - The workExperiences field value
 * @returns {array} - Parsed array of work experiences or empty array
 */
const parseWorkExperiences = (workExperiences) => {
  // If already an array, return it
  if (Array.isArray(workExperiences)) {
    return workExperiences
  }

  // If undefined or null, return empty array
  if (!workExperiences) {
    return []
  }

  // If it's a string, try to parse it
  if (typeof workExperiences === 'string') {
    // Handle "[object Object]" case - this means the object was stringified incorrectly
    if (workExperiences === '[object Object]' || workExperiences.trim() === '[object Object]') {
      return []
    }

    // Try to parse as JSON
    try {
      const parsed = JSON.parse(workExperiences)
      // If parsed result is an array, return it
      if (Array.isArray(parsed)) {
        return parsed
      }
      // If parsed result is an object (single experience), wrap it in array
      if (typeof parsed === 'object' && parsed !== null) {
        return [parsed]
      }
    } catch (e) {
      // If JSON parsing fails, return empty array
      return []
    }
  }

  // Default: return empty array
  return []
}

/**
 * Transform new work experience format to old format
 * @param {object} profile - The applicant profile object
 * @returns {object} - Object with transformed work experience fields
 */
const transformWorkExperience = (profile) => {
  // Check if old format fields already exist (backward compatibility)
  const hasOldFormat = 
    profile.person_position !== undefined ||
    profile.person_startDate !== undefined ||
    profile.person_lastDate !== undefined ||
    profile.person_place_work !== undefined ||
    profile.person_reason_for_leaving !== undefined ||
    profile.fresh_graduate !== undefined

  // If old format exists, use it (backward compatibility)
  if (hasOldFormat) {
    return {
      person_position: profile.person_position || '',
      person_startDate: profile.person_startDate || '',
      person_lastDate: profile.person_lastDate || '',
      person_place_work: profile.person_place_work || '',
      person_reason_for_leaving: profile.person_reason_for_leaving || '',
      fresh_graduate: profile.fresh_graduate || 'false',
    }
  }

  // Transform from new format
  const workType = profile.workType
  const workExperiencesRaw = profile.workExperiences
  const workExperiences = parseWorkExperiences(workExperiencesRaw)

  // If workType is 'without', set as fresh graduate
  if (workType === 'without') {
    return {
      person_position: '',
      person_startDate: '',
      person_lastDate: '',
      person_place_work: '',
      person_reason_for_leaving: '',
      fresh_graduate: 'true',
    }
  }

  // If workType is 'with' and we have work experiences
  if (workType === 'with' && workExperiences.length > 0) {
    const latestExperience = workExperiences[0]
    return {
      person_position: latestExperience.position || '',
      person_startDate: latestExperience.startDate || '',
      person_lastDate: latestExperience.endDate || '',
      person_place_work: latestExperience.companyName || '',
      person_reason_for_leaving: latestExperience.reasonForLeaving || '',
      fresh_graduate: 'false',
      // Pass all work experiences for display
      workExperiences: workExperiences,
    }
  }

  // If workType is 'with' but workExperiences is "[object Object]" (data lost)
  // This happens when the array was incorrectly stringified during form submission
  if (workType === 'with' && typeof workExperiencesRaw === 'string' && 
      (workExperiencesRaw === '[object Object]' || workExperiencesRaw.trim() === '[object Object]')) {
    // Data is lost, but we know they have work experience
    // Return empty fields but indicate they're not a fresh graduate
    return {
      person_position: '[Data unavailable - please contact applicant]',
      person_startDate: '',
      person_lastDate: '',
      person_place_work: '',
      person_reason_for_leaving: '',
      fresh_graduate: 'false',
    }
  }

  // Default: no work experience data
  return {
    person_position: '',
    person_startDate: '',
    person_lastDate: '',
    person_place_work: '',
    person_reason_for_leaving: '',
    fresh_graduate: 'false',
  }
}

const ViewPersonnelPage = () => {
  const [previewData, setPreviewData] = useState([])
  const [loading, setLoading] = useState(true)
  const {id, company} = useParams()
  const [action, setAction] = useState()

  const previewApplicant = async () => {
    const r = await getSpecificApplicants(id, company)
    const data = []
    if (r.length > 0) {
      const s = await getSpecificDocuments(r[0].id, 'all')
      const profile = JSON.parse(r[0].data)
      data.push({
        name: `${profile.person_fname} ${profile.person_mname} ${profile.person_lname}`,
        perm_addr: `${profile.person_permanent_address_street_blk_pres} ${profile.person_permanent_address_street} ${profile.person_permanent_address_zipcode} ${profile.person_permanent_address_city}`,
        pres_addr: `${profile.person_present_address_street_blk} ${profile.person_present_address_street} ${profile.person_present_address_zipcode} ${profile.person_present_address_city}`,
        profile_img: r[0].profile,
        reference_id: r[0].reference_id,
        email: profile.person_email,
        contact: profile.person_contact_no_mob,
        sex: profile.person_sex,
        civil_status: profile.person_civil_status,
        religion: profile.person_rel,
        height: profile.person_ht,
        weight: profile.person_wt,
        blood_type: profile.person_bloodtype,
        tin: profile.person_tin,
        sss: profile.person_sss,
        phl: profile.person_phlhealth,
        pagibig: profile.person_pagibig,
        emerg_name: profile.person_emergency_name,
        emerg_contact: profile.person_emergency_contact_no,
        emerg_relationship: profile.person_emergency_relationship,
        nationality: profile.person_nationality,
        id: r[0].id,
        company: r[0].company,
        // Work Experience - Transform from new format if needed
        ...transformWorkExperience(profile),
        documents: s,
        status: ''
      })
      setLoading(false)
      setPreviewData(data)
      
    }
  }

  const genKey = Math.random()
    .toString(36)
    .substring(2)

  useEffect(() => {
    previewApplicant()
  }, [action])// eslint-disable-line react-hooks/exhaustive-deps
  function verify(value) {
    setAction(value)
  }
  return (
    <div className={`review_application-body ${loading ? `is-loading` : ``}`}>
      {!loading ? (
        <div className="review-container-fluid">
          <div className="row">
            <div className="col-5 form">
              <ViewPersonnelForm verify={verify}/>
            </div>
            <div className="col-7 preview">
              <PreviewCard previewData={previewData} type={5} pageType="personnel" key={genKey}></PreviewCard>
            </div>
          </div>
        </div>
      ) : (
        <Loader />
      )}
    </div>
  )
}

export {ViewPersonnelPage}
